import { FocusMonitor, LiveAnnouncer } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { map } from 'rxjs';
import { ITimeSlotTimestamp } from '../../../models/schedule-calendar.models';
import {
  CALENDAR_PIXELS_PER_MINUTE,
  CalendarAvailableBlock,
  CalendarTimeSlot,
  formatMinuteRange,
  getSlotStart,
  minutesToPixels,
  SELECT_TIME_INCREMENT,
  toBlockSlot,
} from '../../../utils/schedule-calendar.utils';

/** Below these heights the labels don't fit in the block */
const MIN_TIME_LABEL_HEIGHT = 18;
const MIN_PREFERRED_LABEL_HEIGHT = 40;
/** Below this height the delete button overlaps the time label */
const MIN_DELETE_BELOW_TIME_HEIGHT = 40;
/** The select button's height, with its margin from the edge of the block */
const SELECT_BUTTON_HEIGHT = 19;

const MILLISECONDS_PER_MINUTE = 60_000;

/** Gives each block's slot hint its own id */
let nextId = 0;

@Component({
  selector: 'app-schedule-calendar-available-block',
  templateUrl: './schedule-calendar-available-block.component.html',
  styleUrls: ['./schedule-calendar-available-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, NgTemplateOutlet],
  host: {
    '[style.top.px]': 'top()',
    '[style.height.px]': 'height()',
    '[class.keyboard-focused]': 'isKeyboardFocused()',
    '[class.selectable]': 'canSelect()',
    '(mousemove)': 'trackPointer($event)',
    '(mouseleave)': 'pointerMinutes.set(null)',
    '(focusin)': 'isConfirmingSlot = false',
    '(focusout)': 'clearKeyboardSlot($event)',
  },
})
export class ScheduleCalendarAvailableBlockComponent {
  readonly block = input.required<CalendarAvailableBlock>();
  /** Fully in the past, so can no longer be edited */
  readonly isInPast = input(false);
  readonly disabled = input(false);
  /** Read only blocks can't be edited or deleted, only have times selected */
  readonly editable = input(true);
  /**
   * Minutes long of the times that can be selected within the block, shown
   * under the pointer. Times can't be selected without it.
   */
  readonly selectDuration = input<number | null>(null);
  /** Minutes from the start of the day selected times can't start before */
  readonly selectableFrom = input(0);
  /** A selected time still being checked, kept in place when in the block */
  readonly pendingTime = input<ITimeSlotTimestamp | null>(null);
  /**
   * A selected time is being confirmed, so no other can be selected. The slot
   * is kept rather than removed, so it keeps focus for the dialog confirming it
   * to give back once closed.
   */
  readonly busy = input(false);

  readonly edit = output<CalendarAvailableBlock>();
  readonly delete = output<CalendarAvailableBlock>();
  readonly selectTime = output<CalendarTimeSlot>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly injector = inject(Injector);

  private readonly slotButton =
    viewChild<ElementRef<HTMLButtonElement>>('slotButton');
  private readonly selectButton =
    viewChild<ElementRef<HTMLButtonElement>>('selectButton');

  readonly slotHintId = `schedule-calendar-available-block-slot-hint-${nextId++}`;

  /**
   * The dialog confirming a slot takes focus, then gives it back to the slot
   * when it closes, so the slot is kept while it's being confirmed
   */
  protected isConfirmingSlot = false;

  /**
   * Only keyboard focus shows the delete button, as the dialog gives focus
   * back to the block when it closes, even after a mouse click
   */
  readonly isKeyboardFocused = this.monitorKeyboardFocus();

  /** Minutes from the start of the day under the pointer, while over the block */
  readonly pointerMinutes = signal<number | null>(null);

  /** The slot is hidden while the pointer is over the select button, to reach it */
  readonly isOverSelect = signal(false);

  readonly top = computed(() => minutesToPixels(this.block().start));
  readonly height = computed(() =>
    minutesToPixels(this.block().end - this.block().start)
  );

  readonly range = computed(() => formatMinuteRange(this.block()));

  readonly time = computed(() =>
    this.height() >= MIN_TIME_LABEL_HEIGHT ? this.range() : ''
  );

  /**
   * Shown on hover, in place of the preferred label at the bottom, or of the
   * time label when the block is too short for both
   */
  readonly canDelete = computed(
    () => this.editable() && !this.disabled() && !this.isInPast()
  );

  readonly canSelect = computed(
    () => this.selectDuration() !== null && !this.disabled() && !this.isInPast()
  );

  /** The first time that can be selected, when any fits in the block */
  private readonly firstSlotStart = computed(() =>
    this.canSelect()
      ? getSlotStart(
          this.block(),
          this.selectDuration(),
          this.block().start,
          this.selectableFrom()
        )
      : null
  );

  readonly hasSelectableSlot = computed(() => this.firstSlotStart() !== null);

  /**
   * Minutes from the start of the day the slot chosen by keyboard starts at.
   * Cleared when no time can be selected, so it doesn't come back unfocused.
   */
  readonly keyboardMinutes = linkedSignal<boolean, number | null>({
    source: this.hasSelectableSlot,
    computation: () => null,
  });

  readonly isShort = computed(
    () => this.height() < MIN_DELETE_BELOW_TIME_HEIGHT
  );

  readonly preferred = computed(() =>
    this.block().preferred.map((range) => {
      const height = minutesToPixels(range.end - range.start);

      return {
        top: minutesToPixels(range.start - this.block().start),
        height,
        showLabel: height >= MIN_PREFERRED_LABEL_HEIGHT,
        isAtBottom: range.end === this.block().end,
      };
    })
  );

  /**
   * The time that would be selected under the pointer, or else the one chosen
   * by keyboard
   */
  readonly slot = computed(() => {
    const duration = this.selectDuration();
    const minutes =
      (this.isOverSelect() ? null : this.pointerMinutes()) ??
      this.keyboardMinutes();

    if (!this.canSelect() || minutes === null) {
      return null;
    }

    const start = getSlotStart(
      this.block(),
      duration,
      minutes,
      this.selectableFrom()
    );

    if (start === null) {
      return null;
    }

    const slot = toBlockSlot(this.block(), start, duration);

    return {
      slot,
      label: formatMinuteRange(slot),
      top: minutesToPixels(slot.start - this.block().start),
      height: minutesToPixels(duration),
    };
  });

  /**
   * The pending time, when within the block. Its wall clock offset is the
   * block's, as blocks either side of the clocks changing are kept apart.
   */
  readonly pendingSlot = computed(() => {
    const pendingTime = this.pendingTime();
    const block = this.block();

    if (!pendingTime) {
      return null;
    }

    const toMinutes = (timestamp: string) =>
      block.start +
      (Date.parse(timestamp) - Date.parse(block.range.start)) /
        MILLISECONDS_PER_MINUTE;

    const start = toMinutes(pendingTime.start);
    const end = toMinutes(pendingTime.end);

    if (start < block.start || end > block.end) {
      return null;
    }

    return {
      label: formatMinuteRange({ start, end }),
      top: minutesToPixels(start - block.start),
      height: minutesToPixels(end - start),
    };
  });

  /** The select button gives way to a slot covering it, until it's hovered */
  readonly isSelectUnderSlot = computed(() => {
    const slot = this.slot();

    if (!slot || this.isOverSelect()) {
      return false;
    }

    const selectTop = this.isShort() ? 0 : this.height() - SELECT_BUTTON_HEIGHT;

    return (
      slot.top < selectTop + SELECT_BUTTON_HEIGHT &&
      slot.top + slot.height > selectTop
    );
  });

  /** Rounded to the minute, so moving within it doesn't move the slot */
  trackPointer(event: MouseEvent) {
    if (!this.canSelect() || this.busy()) {
      return;
    }

    const { top } = this.elementRef.nativeElement.getBoundingClientRect();

    this.pointerMinutes.set(
      this.block().start +
        Math.floor((event.clientY - top) / CALENDAR_PIXELS_PER_MINUTE)
    );
  }

  /** Shows the first time that can be selected, for the keyboard to move */
  startKeyboardSlot() {
    if (this.busy()) {
      return;
    }

    this.keyboardMinutes.set(this.firstSlotStart());

    afterNextRender(() => this.focusVia(this.slotButton()), {
      injector: this.injector,
    });
  }

  moveKeyboardSlot(event: KeyboardEvent) {
    const slot = this.slot();

    if (!slot || this.busy()) {
      return;
    }

    if (event.key === 'Escape') {
      this.focusVia(this.selectButton());
      this.keyboardMinutes.set(null);
      return;
    }

    const at = {
      ArrowUp: slot.slot.start - SELECT_TIME_INCREMENT,
      ArrowDown: slot.slot.start + SELECT_TIME_INCREMENT,
      Home: this.block().start,
      End: this.block().end,
    }[event.key];

    if (at === undefined) {
      return;
    }

    // Stops the calendar scrolling
    event.preventDefault();

    const start = getSlotStart(
      this.block(),
      this.selectDuration(),
      at,
      this.selectableFrom()
    );

    // Moving from where the pointer left it, which stops driving it
    this.pointerMinutes.set(null);
    this.keyboardMinutes.set(start);

    const moved = this.slot();

    if (moved && start !== slot.slot.start) {
      this.liveAnnouncer.announce(`Select time ${moved.label}`);
    }
  }

  confirmSlot(slot: CalendarTimeSlot) {
    if (this.busy()) {
      return;
    }

    this.isConfirmingSlot = true;
    this.selectTime.emit(slot);
  }

  /**
   * The slot chosen by keyboard goes once focus leaves the block, unless it's
   * still being confirmed
   */
  clearKeyboardSlot({ relatedTarget }: FocusEvent) {
    if (
      !(this.isConfirmingSlot && this.busy()) &&
      !this.elementRef.nativeElement.contains(relatedTarget as Node | null)
    ) {
      this.keyboardMinutes.set(null);
    }
  }

  private focusVia(element: ElementRef<HTMLElement> | undefined) {
    if (element) {
      this.focusMonitor.focusVia(element, 'keyboard');
    }
  }

  private monitorKeyboardFocus() {
    const focusMonitor = inject(FocusMonitor);
    const elementRef = inject(ElementRef);

    inject(DestroyRef).onDestroy(() => focusMonitor.stopMonitoring(elementRef));

    return toSignal(
      focusMonitor
        .monitor(elementRef, true)
        .pipe(map((origin) => origin === 'keyboard')),
      { initialValue: false }
    );
  }
}
